import { getFirebase } from 'firestorter';

import Collections from '../../utils/collections';
import { AttachmentType } from '../../utils/attachments';
import feedType from '../context/FeedContext/config/feedTypes';
import { activityStatus } from './CoachActivity';
import { CoachTaskPriority, CoachTaskType } from './CoachTask';

const HighlightItemType = {
  CHAT_QUOTE: 'CHAT_QUOTE',
  /** An answer the client wrote on a check-in. Renders as a quote; it names the check-in, not a message. */
  CHECK_IN_ANSWER: 'CHECK_IN_ANSWER',
  PHOTO: 'PHOTO',
  PHOTO_COMPARE: 'PHOTO_COMPARE',
};

const HighlightItemOrigin = {
  CHAT: 'CHAT',
  CHECK_IN: 'CHECK_IN',
};

const HighlightStatus = {
  READY: 'READY',
  /** Waiting for the client to agree in the app. Its coach task is held back until they do. */
  PENDING_CLIENT_CONSENT: 'PENDING_CLIENT_CONSENT',
  /** The client said no in the app. Their app removes the coach task in the same write; the crops stay. */
  DECLINED: 'DECLINED',
  /** The AC removed the draft before the client answered, so the app stops asking. */
  WITHDRAWN: 'WITHDRAWN',
};

const DEFAULT_TASK_TITLE = 'New highlight to share';

const buildTaskHighlight = (items) => {
  const attachments = [];
  let text = '';

  items.forEach((item) => {
    const quote = item.design?.widgetConfigs?.chatBubble?.text;
    if (quote && !text) {
      text = quote;
    }
    // `title` is what AttachmentSlider keys on, so it has to be unique across the snapshot.
    [item.photo, item.before, item.after].forEach((photo, index) => {
      if (photo?.assetPath) {
        attachments.push({
          type: AttachmentType.IMAGE,
          assetPath: photo.assetPath,
          title: `${item.id}-${index}`,
          mimeType: 'image/jpeg',
        });
      }
    });
  });

  return { attachments, ...(text ? { text } : {}) };
};

class ContentHighlight {
  static async createWithTask({
    userId,
    coachId,
    items,
    createdBy,
    createdByName,
    title,
    userName,
    userAvatar,
    consent,
    requiresClientApproval = false,
    clientMessage = '',
  }) {
    // Through firestorter's app, the same one every Model writes with — and the one tests can stand in for.
    const { firestore } = getFirebase().default;
    const db = firestore();
    const batch = db.batch();

    const highlightRef = db.collection(Collections.CONTENT_HIGHLIGHT).doc();
    const taskRef = db.collection(Collections.COACH_ACTIVITY).doc();
    const createdAt = firestore.FieldValue.serverTimestamp();

    /*
      The client asked to be asked, so both documents start as drafts. Only the client's app moves them on,
      writing both in one batch: the highlight is what the client is shown, and the
      task stays out of every list the coach sees until then. A draft carries no `consent` — the app writes
      the client's own answer on a yes, rather than this asserting one nobody has given yet. Nothing expires
      a draft: one the client never answers stays waiting until the AC removes it, which withdraws it.
    */

    const highlightData = {
      coach: coachId,
      user: userId,
      createdBy,
      createdByName: createdByName || '',
      createdAt,
      status: requiresClientApproval ? HighlightStatus.PENDING_CLIENT_CONSENT : HighlightStatus.READY,
      taskId: taskRef.id,
      items,
      ...(consent && !requiresClientApproval ? { consent } : {}),
      // The AC's words under the photos when the app asks the client. Only on a draft, and only when written.
      ...(requiresClientApproval && clientMessage ? { clientMessage } : {}),
    };

    const taskData = {
      type: feedType.COACH_TASK,
      status: requiresClientApproval ? activityStatus.AWAITING_CLIENT_CONSENT : activityStatus.IN_PROGRESS,
      createdAt,
      time: createdAt,
      coach: coachId,
      user: userId,
      content: '',
      taskType: CoachTaskType.SOCIAL_MEDIA_POST,
      taskTitle: title || DEFAULT_TASK_TITLE,
      priority: CoachTaskPriority.MEDIUM,
      assignedBy: createdBy,
      assignedByName: createdByName || '',
      // `highlightId` is the pointer, `highlight` is the snapshot the dashboard draws from.
      highlightId: highlightRef.id,
      highlight: buildTaskHighlight(items),
      // Subject display data for the feed header (UserProfileCard reads these). Firestore rejects
      // undefined, so these are only set when the client's record actually has them.
      ...(userName ? { userName } : {}),
      ...(userAvatar ? { userAvatar } : {}),
    };

    batch.set(highlightRef, highlightData);
    batch.set(taskRef, taskData);

    /*
      Throws to the caller, which cleans up the crops it already uploaded. Worth knowing when reading a
      failure: the batch commits as a unit and each document is rules-checked separately, so the error
      never says which of the two was rejected — a `permission-denied` here is as likely to be the
      coach task as the highlight.
    */
    await batch.commit();

    return { highlightId: highlightRef.id, taskId: taskRef.id };
  }

  /**
   * Withdraws a highlight still waiting on the client, removing its coach task with it, so their app stops
   * asking about a photo no coach will ever get. One batch, so the task is never removed while its highlight
   * still asks. The remover is required, and the withdrawal is stamped with the server's time. The crops stay, as
   * for any removed task. The admin page is the only place a waiting task is shown, and so the only caller.
   */
  static async withdrawDraft({ taskId, highlightId, removedBy }) {
    if (!removedBy) {
      throw new Error('Withdrawing a highlight draft needs the id of who removed it');
    }
    const { firestore } = getFirebase().default;
    const db = firestore();
    const now = firestore.FieldValue.serverTimestamp();
    const batch = db.batch();

    batch.update(db.collection(Collections.COACH_ACTIVITY).doc(taskId), {
      status: activityStatus.REMOVED,
      removedAt: now,
      removedBy,
    });
    batch.update(db.collection(Collections.CONTENT_HIGHLIGHT).doc(highlightId), {
      status: HighlightStatus.WITHDRAWN,
      withdrawnAt: now,
      withdrawnBy: removedBy,
    });

    await batch.commit();
  }
}

export default ContentHighlight;
export {
  HighlightItemType,
  HighlightItemOrigin,
  HighlightStatus,
};
